<script lang="ts" setup>
import {computed, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {QBtnDropdown, QItem, QItemSection} from 'quasar';
import {STANDALONE_INTERVENTION, type JournalInterventionDTO} from '@/ts/types/dto/journals-dto';

/**
 * Narrows the journal list to one intervention, or to the entries that belong to none (IPD-3107).
 *
 * The options come from the backend rather than from the user's enrolments: it returns the
 * interventions they actually have entries under, so the list never offers a choice that would
 * empty the page. Built on the same `q-btn-dropdown` as the period filter beside it, because the
 * three read as one row of controls.
 */
const props = defineProps<{
  /** From the journals response; already narrowed to the period on screen. */
  available: JournalInterventionDTO[];
  /** Whether to offer "outside of intervention" at all. */
  hasStandalone: boolean;
}>();

/** Null means no narrowing; {@link STANDALONE_INTERVENTION} means "belonging to none". */
const selected = defineModel<string | null>({default: null});

const {t: $t} = useI18n();

interface Option {
  label: string;
  value: string | null;
}

/**
 * Every intervention name this dropdown has been handed, kept past the facet that carried it.
 *
 * The facet only covers the period on screen, so switching from "Last year" to "Last week" can
 * drop the very intervention the list is still filtered by. Without a name for it the button fell
 * back to its first option and read "All interventions" over a filtered — usually empty — list,
 * which is the page stating the opposite of what it is doing.
 */
const knownNames = ref(new Map<string, string>());

watch(() => props.available, available => {
  available.forEach(intervention => knownNames.value.set(intervention.id, intervention.name));
}, {immediate: true, deep: true});

const options = computed<Option[]>(() => {
  const available = props.available.map(
    intervention => ({label: intervention.name, value: intervention.id as string | null}),
  );
  // A selection the current period no longer offers stays listed, so it can be read and undone.
  const orphan = selected.value !== null
    && selected.value !== STANDALONE_INTERVENTION
    && !props.available.some(intervention => intervention.id === selected.value)
    ? [{label: knownNames.value.get(selected.value) ?? $t('journal_filter_unknown_intervention'), value: selected.value}]
    : [];

  return [
    {label: $t('journal_filter_all_interventions'), value: null},
    ...available,
    ...orphan,
    ...(props.hasStandalone || selected.value === STANDALONE_INTERVENTION
      ? [{label: $t('journal_filter_outside_intervention'), value: STANDALONE_INTERVENTION}]
      : []),
  ];
});

const current = computed(() => options.value.find(o => o.value === selected.value) ?? options.value[0]);

function select(value: string | null): void {
  if (value !== selected.value) {
    selected.value = value;
  }
}
</script>

<template>
  <q-btn-dropdown
    class="intervention-filter"
    flat
    dense
  >
    <template #label>
      <div class="selected-filter text-body">
        <span class="label">{{ current.label }}</span>
      </div>
    </template>

    <!--
      Quasar 2.28 stopped putting role="menu" on the dropdown content, so the list carries it:
      `menuitemradio` rows are only valid inside a menu, and one filter is chosen at a time.
    -->
    <q-list role="menu">
      <q-item
        v-for="option in options"
        :key="option.value ?? 'all'"
        v-close-popup
        clickable
        role="menuitemradio"
        :aria-checked="option.value === selected"
        :class="{'selected-option': option.value === selected}"
        @click="select(option.value)"
      >
        <q-item-section>
          {{ option.label }}
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>
.intervention-filter {
  @include focus-visible;
  padding: var(--Spacing-spacing-1) var(--Spacing-spacing-3);
  border-radius: var(--button-border-radius);
}

.selected-filter {
  display: flex;
  align-items: center;
}

.label {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-100);
}

.selected-option {
  background-color: var(--Secondary-secondary-color-1);
  color: var(--Neutral-UI-neutral-ui-100);
}
</style>
